/* eslint-disable */
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Card,
  Col,
  message,
  Modal,
  Row,
  Space,
  Spin,
  Typography
} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import React, { useMemo, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import InfoCard from '../InfoCard';
import TimekeepingDetail from '../Timekeeping';
import SalaryCalculatingView from './SalaryCalculatingView';
import SalaryTableInfo from './SalaryTableInfo';

import { useAppSelector } from 'app/store';
import CategoryCalculate from 'common/components/CategoryCalculate';
import HeaderPage from 'common/components/HeaderPage';
import { getApproveDepartmentSalaryDetailService, getApproveSalaryDetailService, getHistoryApproveSalaryDetailService } from 'common/services/extends/approveSalary';
import { acApproveSalaryByIdService, ktApproveSalaryByIdService, ktUpdateSalaryByIdService, opApproveSalaryByIdService, reopenPayrollPeriodService } from 'common/services/extends/payrollPeriods';
import { ApproveParamsTypes, KTApproveParamsTypes, KTUpdateParamsTypes } from 'common/services/extends/payrollPeriods/types';
import { handleStatusHistoryLabel, handleStatusLabel, Level, ROUTE_PATHS } from 'common/utils/constant';
import { detectError, detectErrorByCode, formatDateTime } from 'common/utils/functions';
import { ketoanApproveSchema, reasonRevokeSchema } from 'common/utils/schemas';
import roles, { getPermission } from 'configs/roles';

export type KTApproveParamsForm = {
  codeDetail: string;
  bankId?: OptionType,
  dateTransfer: string,
  methodTransfer?: OptionType,
  description: string;
};

interface Props extends ActiveRoles {
  isHistory?: boolean
  updateInfoTableSalary?: boolean;
}

const ApproveSalaryDetailTemplate: React.FC<Props> = ({ isHistory, updateInfoTableSalary }) => {
  /* Hooks */
  const rolesUser = useAppSelector((state) => state.auth.roles);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [modal, setModal] = useState(false);

  const payrollPeriodId = searchParams.get('payrollPeriodId') || '';
  const approveLogId = searchParams.get('approveLogId') || '';

  /* React-hook-form */
  const methodNote = useForm<{ note: string, id: number | null }>({
    defaultValues: {
      note: '',
      id: null
    },
    mode: 'onChange',
    resolver: yupResolver(reasonRevokeSchema)
  });

  const methodTransferOptions: OptionType[] = [
    {
      label: t('approveSalary.cash'),
      value: 1
    },
    {
      label: t('approveSalary.tranfer'),
      value: 2
    },
  ];

  const defaultValues: KTApproveParamsForm = {
    codeDetail: '',
    dateTransfer: '',
    description: '',
    methodTransfer: methodTransferOptions[1],
    bankId: undefined
  };

  const queryKey = ['approve-salary-detail', payrollPeriodId];

  const methodTranferLabel = (key: number) => {
    switch (key) {
      case 1:
        return t('approveSalary.cash')
      case 2:
        return t('approveSalary.tranfer')
      default:
        return '';
    }
  }

  const { isLoading, data } = useQuery(
    queryKey,
    () => {
      if (updateInfoTableSalary) {
        return getApproveSalaryDetailService(payrollPeriodId, {
          _getApproved: 1,
        });
      }
      if (isHistory) {
        return getHistoryApproveSalaryDetailService(approveLogId);
      }
      return getApproveDepartmentSalaryDetailService(payrollPeriodId);
    },
    {
      enabled: !!payrollPeriodId,
      onSuccess: (res) => {
        if (res) {
          methodAccount.reset({
            codeDetail: res?.payrollPeriodData?.codeDetail,
            dateTransfer: res?.payrollPeriodData?.dateTransfer,
            description: res?.payrollPeriodData?.description,
            methodTransfer: isHistory ? res?.payrollPeriodData?.methodTransfer ? {
              label: methodTranferLabel(res?.payrollPeriodData?.methodTransfer),
              value: res?.payrollPeriodData?.methodTransfer
            } : undefined : {
              label: methodTranferLabel(res?.payrollPeriodData?.methodTransfer || 2),
              value: res?.payrollPeriodData?.methodTransfer || 2
            },
            bankId: res?.bankData ? {
              label: res?.bankData?.bankName || '',
              value: res?.bankData?.bankId
            } : undefined
          });
        } else {
          methodAccount.reset(defaultValues);
        }
      },
      onError: (error: unknown) => {
        if (error instanceof AxiosError) {
          const status = error.response?.status;
          message.error(t(detectError(status)));
        }
      }
    }
  );

  const methodAccount = useForm<KTApproveParamsForm>({
    defaultValues,
    mode: 'onChange',
    resolver: data?.currentLevel === 4 ? yupResolver(ketoanApproveSchema) : undefined
  });

  const { mutate: acApproveSalaryMutate, isLoading: acApproveSalaryLoading } = useMutation(
    ['acApproveSalary'],
    async (params: ApproveParamsTypes) => acApproveSalaryByIdService(payrollPeriodId, params),
    {
      onSuccess: () => {
        message.success(t('message.approveSuccess'));
        navigate(ROUTE_PATHS.APPROVE_SALARY_MANAGEMENT);
      },
      onError: () => {
        message.error(t('message.approveError'));
      }
    }
  );

  const { mutate: opApproveSalaryMutate, isLoading: opApproveSalaryLoading } = useMutation(
    ['opApproveSalary'],
    async (params: ApproveParamsTypes) => opApproveSalaryByIdService(payrollPeriodId, params),
    {
      onSuccess: () => {
        message.success(t('message.approveSuccess'));
        navigate(ROUTE_PATHS.APPROVE_SALARY_MANAGEMENT);
      },
      onError: () => {
        message.error(t('message.approveError'));
      }
    }
  );

  const { mutate: ktApproveSalaryMutate, isLoading: ktApproveSalaryLoading } = useMutation(
    ['ktApproveSalary'],
    async (params: KTApproveParamsTypes) => ktApproveSalaryByIdService(payrollPeriodId, params),
    {
      onSuccess: () => {
        message.success(t('message.approveSuccess'));
        navigate(ROUTE_PATHS.APPROVE_SALARY_MANAGEMENT);
      },
      onError: () => {
        message.error(t('message.approveError'));
      }
    }
  );

  const { mutate: ktUpdateSalaryMutate, isLoading: ktUpdateSalaryLoading } = useMutation(
    ['ktUpdateSalary'],
    async (params: KTUpdateParamsTypes) => ktUpdateSalaryByIdService(payrollPeriodId, params),
    {
      onSuccess: () => {
        message.success(t('message.updateSuccess'));
        navigate(ROUTE_PATHS.APPROVED_SALARY_MANAGEMENT);
      },
      onError: (error: ErrorResponse[]) => {
        if (error.length > 0) {
          error.forEach((
            e
          ) => {
            methodAccount.setError(
              e.field as keyof KTApproveParamsForm,
              { message: t(detectErrorByCode(e.code, e.field, e.message)) }
            );
            message.error(
              t(detectErrorByCode(e.code, e.field, e.message))
            );
          });
        } else {
          message.error(t('message.updateError'));
        }
      }
    }
  );

  const { mutate: reopenSalaryMutate, isLoading: reopenSalaryLoading } = useMutation(
    ['reopenSalary'],
    async () => reopenPayrollPeriodService(payrollPeriodId),
    {
      onSuccess: () => {
        message.success(t('message.updateSuccess'));
        navigate(ROUTE_PATHS.APPROVED_SALARY_MANAGEMENT);
      },
      onError: () => {
        message.error(t('message.updateError'));
      }
    }
  );

  /* Functions */
  const handleSubmitNote = async () => {
    const isValid = await methodNote.trigger();
    if (!isValid) return;
    const formData = methodNote.getValues();
    if (getPermission(rolesUser, roles.OP_APPROVE_SALARY) && data?.currentLevel === 2) {
      opApproveSalaryMutate({
        approve: false,
        note: formData.note
      });
    } else if (getPermission(rolesUser, roles.AC_APPROVE_SALARY) && data?.currentLevel === 3) {
      acApproveSalaryMutate({
        approve: false,
        note: formData.note
      });
    } else if (getPermission(rolesUser, roles.KT_APPROVE_SALARY) && data?.currentLevel === 4) {
      ktApproveSalaryMutate({
        approve: false,
        note: formData.note,
      });
    }
  };

  const handleApprove = async () => {
    if (updateInfoTableSalary && data?.currentLevel === 4) {
      const formData = methodAccount.getValues();
      ktUpdateSalaryMutate({
        dateTransfer: formData.dateTransfer ? dayjs(formData.dateTransfer).format('YYYY-MM-DD') : undefined,
        codeDetail: formData?.codeDetail || undefined,
        description: formData?.description || undefined,
      });
      return;
    }
    if (getPermission(rolesUser, roles.OP_APPROVE_SALARY) && data?.currentLevel === 2) {
      opApproveSalaryMutate({
        approve: true,
      });
    } else if (getPermission(rolesUser, roles.AC_APPROVE_SALARY) && data?.currentLevel === 3) {
      acApproveSalaryMutate({
        approve: true,
      });
    } else if (getPermission(rolesUser, roles.KT_APPROVE_SALARY) && data?.currentLevel === 4) {
      const formData = methodAccount.getValues();
      ktApproveSalaryMutate({
        approve: true,
        bankId: formData.bankId?.value ? Number(formData.bankId?.value) : undefined,
        dateTransfer: formData.dateTransfer ? dayjs(formData.dateTransfer).format('YYYY-MM-DD') : undefined,
        methodTransfer: Number(formData.methodTransfer?.value),
        codeDetail: formData?.codeDetail || undefined,
        description: formData?.description || undefined,
      });
    }
  };

  const dataByStatusApprove = useMemo(() => {
    if (data?.payrollPeriodData?.statusApprove === 3) return data?.approvesData?.[data?.approvesData.length - 1];
    if (data?.payrollPeriodData?.statusApprove === 4) return data?.approvesData?.find((f) => f.approve === false);
  }, [data]);

  const opPermission = getPermission(rolesUser, roles.OP_APPROVE_SALARY) && (data?.currentLevel || data?.payrollPeriodData?.levelApprove) === 2;
  const acPermission = getPermission(rolesUser, roles.AC_APPROVE_SALARY) && (data?.currentLevel || data?.payrollPeriodData?.levelApprove) === 3;
  const ketoanPermission = getPermission(rolesUser, roles.KT_APPROVE_SALARY) && (data?.currentLevel || data?.payrollPeriodData?.levelApprove) === 4;

  const actionHeader = useMemo(() => {
    if (updateInfoTableSalary) {
      return (
        <Space>
          {
            getPermission(rolesUser, '*') && (
              <Button
                type="primary"
                disabled={reopenSalaryLoading}
                onClick={async () => {
                  Modal.confirm({
                    className: 't-pagetable_deleteRecordModal',
                    autoFocusButton: 'cancel',
                    okText: t('system.ok'),
                    cancelText: t('system.cancel'),
                    cancelButtonProps: {
                      type: 'primary',
                    },
                    okButtonProps: {
                      type: 'default',
                    },
                    title: t('message.confirmReopenSalary'),
                    onOk: () => reopenSalaryMutate(),
                  });
                }}
              >
                {t('salary.reopen')}
              </Button>
            )
          }
          <Button
            type="primary"
            disabled={ktUpdateSalaryLoading || (!ketoanPermission)}
            onClick={async () => {
              if (data?.currentLevel === 4) {
                const isValid = await methodAccount.trigger();
                if (!isValid) return;
              }
              Modal.confirm({
                className: 't-pagetable_deleteRecordModal',
                autoFocusButton: 'cancel',
                okText: t('system.ok'),
                cancelText: t('system.cancel'),
                cancelButtonProps: {
                  type: 'primary',
                },
                okButtonProps: {
                  type: 'default',
                },
                title: t('message.confirmSave'),
                onOk: () => handleApprove(),
              });
            }}
          >
            {t('system.update')}
          </Button>
        </Space>
      )
    }
    if (data && (data.payrollPeriodData?.statusApprove === 3 || data.payrollPeriodData?.statusApprove === 4) && !updateInfoTableSalary) {
      return null;
    }
    return (
      <Space size={16}>
        <Button
          type="primary"
          disabled={acApproveSalaryLoading
            || opApproveSalaryLoading
            || ktApproveSalaryLoading
            || (!opPermission && !acPermission && !ketoanPermission)}
          onClick={async () => {
            if (data?.currentLevel === 4) {
              const isValid = await methodAccount.trigger();
              if (!isValid) return;
            }
            Modal.confirm({
              className: 't-pagetable_deleteRecordModal',
              autoFocusButton: 'cancel',
              okText: t('system.ok'),
              cancelText: t('system.cancel'),
              cancelButtonProps: {
                type: 'primary',
              },
              okButtonProps: {
                type: 'default',
              },
              title: t('message.confirmApproveTableSalary'),
              onOk: () => handleApprove(),
            });
          }}
        >
          {data?.currentLevel === 4 ? t('system.saveApproved') : t('system.sendApprove')}
        </Button>
        <Button
          type="default"
          danger
          onClick={() => setModal(true)}
          disabled={acApproveSalaryLoading
            || opApproveSalaryLoading
            || ktApproveSalaryLoading
            || (!opPermission && !acPermission && !ketoanPermission)}
        >
          {t('salary.revoke')}
        </Button>
      </Space>
    )
  }, [
    data,
    opApproveSalaryLoading,
    acApproveSalaryLoading,
    ktApproveSalaryLoading,
    opPermission,
    acPermission,
    ketoanPermission,
    updateInfoTableSalary,
    methodAccount,
    reopenSalaryLoading,
  ]);

  const title = useMemo(() => {
    if (isHistory) {
      return t('approveSalary.historyApproveDetail');
    }
    if (updateInfoTableSalary) {
      return t('approveSalary.historyApprovedDetail');
    }
    return t('approveSalary.titleApproveSalary');
  }, [isHistory, updateInfoTableSalary, t]);

  /* Render */
  return (
    <>
      <HeaderPage
        fixed
        title={title}
        rightHeader={isHistory ? null : actionHeader}
      />
      <Spin spinning={isLoading}>
        <div className="t-mainlayout_wrapper">
          <Space direction="vertical" style={{ width: '100%' }}>
            {isHistory ? (
              <Card>
                <Row gutter={16}>
                  <Col xs={24} lg={12} className="u-mb-8" style={{ display: 'flex', alignItems: 'center' }}>
                    <Typography.Text>
                      {t('salary.approvedStatus')}:
                    </Typography.Text>
                    <Typography.Text
                      strong
                      style={{ width: 'fit-content', display: 'inline-block', marginLeft: 8 }}
                      className={`salary-statusApproved${` ${isHistory ? data?.payrollPeriodData?.isReopen ? 'reopen' : data?.payrollPeriodData?.approve ? 'approved' : 'refuse' : t(handleStatusLabel(data?.payrollPeriodData?.statusApprove) || '')}`}`}
                    >
                      {isHistory ? t(handleStatusHistoryLabel(data?.payrollPeriodData?.approve, data?.payrollPeriodData?.isReopen) || '') : t(handleStatusLabel(data?.payrollPeriodData?.statusApprove) || '')}
                    </Typography.Text>
                  </Col>
                  <Col xs={24} lg={12} className="u-mb-8" style={{ display: 'flex', alignItems: 'center' }}>
                    <Typography.Text >
                      {t('salary.at')}:
                    </Typography.Text>
                    <Typography.Text strong style={{ width: 'fit-content', display: 'inline-block', marginLeft: 8 }}>
                      {formatDateTime(data?.payrollPeriodData?.updatedAt)}
                    </Typography.Text>
                  </Col>
                  <Col xs={24} lg={12} className="u-mb-8" style={{ display: 'flex', alignItems: 'center' }}>
                    <Typography.Text >
                      {t('approveSalary.levelApprove')}:
                    </Typography.Text>
                    <Typography.Text strong style={{ width: 'fit-content', display: 'inline-block', marginLeft: 8 }}>
                      {data?.payrollPeriodData?.levelApprove ? t(Level[data?.payrollPeriodData?.levelApprove - 1].label) : ''}
                    </Typography.Text>
                  </Col>
                  <Col xs={24} lg={12} className="u-mb-8" style={{ display: 'flex', alignItems: 'center' }}>
                    <Typography.Text >
                      {t('approveSalary.userApprove')}:
                    </Typography.Text>
                    <Typography.Text strong style={{ width: 'fit-content', display: 'inline-block', marginLeft: 8 }}>
                      {data?.userApprove?.name || ''}
                    </Typography.Text>
                  </Col>
                  {dataByStatusApprove?.note && (
                    <Col className="u-mt-8" span={24} style={{ display: 'flex', alignItems: 'center' }}>
                      <Typography.Text>
                        {t('salary.reasonRevoke')}:
                      </Typography.Text>
                      <Typography.Text strong style={{ width: 'fit-content', display: 'inline-block', marginLeft: 8 }}>
                        {dataByStatusApprove.note}
                      </Typography.Text>
                    </Col>
                  )}
                </Row>
              </Card>
            ) : null}
            <InfoCard data={data} />
            <TimekeepingDetail
              endDate={data?.payrollPeriodData?.endDate}
              fromDate={data?.payrollPeriodData?.startDate}
              name={data?.payrollPeriodData?.name}
            />
            {((updateInfoTableSalary && getPermission(rolesUser, '*')) || ketoanPermission) && (
              <SalaryTableInfo
                methodAccount={methodAccount}
                disabled={updateInfoTableSalary}
                viewOnly={isHistory}
              />
            )}
            <SalaryCalculatingView />
            <CategoryCalculate view />
          </Space>
        </div>
      </Spin>
      <Modal
        title={(
          <Typography.Title
            level={4}
          >
            {t('salary.reasonRevoke')}
          </Typography.Title>
        )}
        open={modal}
        width={800}
        onCancel={() => {
          setModal(false);
          methodNote.reset({
            note: '',
            id: null
          });
        }}
        onOk={() => handleSubmitNote()}
      >
        <FormProvider {...methodNote}>
          <Controller
            name="note"
            render={({
              field: { value, onChange }, fieldState: { error }
            }) => (
              <>
                <TextArea
                  className="u-mt-8"
                  value={value}
                  onChange={onChange}
                  size="large"
                  rows={6}
                  style={{ minHeight: 50 }}
                />
                {error && (
                  <span
                    className="a-input_errorMessage"
                  >
                    {error.message}
                  </span>
                )}
              </>
            )}
          />
        </FormProvider>
      </Modal>
    </>
  );
};

export default ApproveSalaryDetailTemplate;
